import type { AccountCreditGrantsCreditGrant } from '@customerio/hydra-ui-api';

type CreditGrantBalance = Pick<AccountCreditGrantsCreditGrant, 'credits_remaining'> &
  Partial<Pick<AccountCreditGrantsCreditGrant, 'expires_at' | 'starts_at'>>;

type CreditGrantOriginal = Pick<AccountCreditGrantsCreditGrant, 'credits_original'> & CreditGrantBalance;

/**
 * Whether a credit grant can be spent right now.
 *
 * Two things disqualify a grant, and both matter: one that has expired, and one that is
 * *scheduled* — `starts_at` in the future. Counting scheduled grants overstates the balance, which
 * matters most where a balance feeds a cap (the Builder top-up is capped at $1,000, so an inflated
 * balance wrongly blocks a purchase the account is entitled to make).
 *
 * Boundaries are whole-second, matching the grant timestamps, so a balance and any projection built
 * from the same grants agree on which are active.
 *
 * Both credit endpoints (`accounts/llm_credits`, `accounts/builder_credits`) return this same grant
 * shape, so both balances derive from this one predicate rather than a copy each.
 */
export function isSpendableGrant(grant: CreditGrantBalance, nowSec: number): boolean {
  // Both timestamps are optional-access on purpose even though the wire contract marks them
  // required. This predicate runs on the workspace home screen, so a grant missing either field
  // would take that page down; an absent bound is treated as "no bound", which is the safe reading.
  const isExpired = grant.expires_at?.Valid === true && grant.expires_at.Timestamp < nowSec;
  return !isExpired && hasGrantStarted(grant, nowSec);
}

/** True when the grant has reached its `starts_at`. Unlike {@link isSpendableGrant}, it keeps expired grants. */
export function hasGrantStarted(grant: Partial<Pick<AccountCreditGrantsCreditGrant, 'starts_at'>>, nowSec: number) {
  return !(grant.starts_at?.Valid === true && grant.starts_at.Timestamp > nowSec);
}

/** Sums the spendable balance across grants. Raw counts — LLM credits are not cents. */
export function spendableGrantTotal(grants: CreditGrantBalance[], nowSec: number): number {
  return grants.filter((grant) => isSpendableGrant(grant, nowSec)).reduce((total, g) => total + g.credits_remaining, 0);
}

/**
 * Sums the funds originally granted across spendable grants — the total a usage meter fills toward.
 *
 * Uses the same spendable predicate as the balance, so used (total minus remaining) never counts an
 * expired or scheduled grant on one side but not the other.
 */
export function spendableGrantOriginalTotal(grants: CreditGrantOriginal[], nowSec: number): number {
  return grants.filter((grant) => isSpendableGrant(grant, nowSec)).reduce((total, g) => total + g.credits_original, 0);
}
